import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { manufacturersApi } from './api'
import { handleApiError } from '@/lib/api/axios'
import { useToast } from '@/hooks/use-toast'

export function useManufacturers() {
    return useQuery({
        queryKey: ['manufacturers'],
        queryFn: async () => {
            const response = await manufacturersApi.list()
            return response.data.data
        },
    })
}

export function useManufacturer(id: number | null) {
    return useQuery({
        queryKey: ['manufacturers', id],
        queryFn: async () => {
            if (!id) return null
            const response = await manufacturersApi.get(id)
            return response.data.data
        },
        enabled: !!id,
    })
}

export function useCreateManufacturer() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: manufacturersApi.create,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['manufacturers'] })
            toast({
                title: 'Success',
                description: 'Manufacturer created successfully',
            })
        },
        onError: (error) => {
            handleApiError(error)
        },
    })
}

export function useUpdateManufacturer() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: { name: string; icon?: number | null } }) =>
            manufacturersApi.update(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['manufacturers'] })
            toast({
                title: 'Success',
                description: 'Manufacturer updated successfully',
            })
        },
        onError: (error) => {
            handleApiError(error)
        },
    })
}

export function useDeleteManufacturer() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: manufacturersApi.delete,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['manufacturers'] })
            toast({
                title: 'Success',
                description: 'Manufacturer deleted successfully',
            })
        },
        onError: (error) => {
            handleApiError(error)
        },
    })
}
